import { useEffect, useState } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { ArticleEntryData } from "@/types/articleEntryData.types";
import { DealsSection } from "@/types/deals.types";
import { useScroll } from "@FillInTableContainer/components/FillInTable/hooks/useScroll";
import { TableCreatorTextFieldContainer } from "@FillInTableContainer/shared";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import useCheckPressedShell from "@FillInTableContainer/shared/hooks/useCheckPressedShell";
import { useGetInfoOfShell } from "@FillInTableContainer/shared/hooks/useGetInfoOfShell";
import { getShellPlaceholder } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getShellTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import VfAssetTransferTerritoryModal from "./components/VfAssetTransferTerritoryModal/VfAssetTransferTerritoryModal";
import * as S from "./VfAssetTransferShell.style";
import { useHandleVfAssetTransferTableCreatorShell } from "./hooks/useHandleVfAssetTransferTableCreatorShell";
import { ASSET_TRANSFER_COLUMN_WIDTH } from "../DealTermsVentureFormationGroupShell/constants/assetTransferColumns.constants";

export interface PositionInfoProps {
  groupTableKey: DealsSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  groupShellIndex: number;
  subGroupShellIndex: number;
  subGroupShellKey: string | null;
  tableKey: string | null;
  tableIndex: number;
  shellKey: string;
  shellIndex: number;
}

interface Props extends PositionInfoProps {
  subGroupShellIndex: number;
  subGroupShellKey: string | null;
  isFocusedGroupShell: boolean;
  isClearGroupShell: boolean;
  isDeleteGroupShell: boolean;
  isAlertGroupShell: boolean;

  isClearSubGroupShell: boolean;
  isDeleteSubGroupShell: boolean;
  isFocusGroupShellContainer: boolean;
}

const articleSection = ENTRY_DATA_SECTION_KEYS.deals;

const VfAssetTransferShell = ({
  groupTableKey,
  groupTableIndex,
  groupShellKey,
  groupShellIndex,
  tableKey,
  shellKey,
  tableIndex,
  shellIndex,
  subGroupShellIndex,
  subGroupShellKey,
  isClearGroupShell,
  isDeleteGroupShell,
  isAlertGroupShell,
  isClearSubGroupShell,
  isDeleteSubGroupShell,
  isFocusGroupShellContainer,
}: Props) => {
  const draftArticleId = getPressIdFromURL();
  const [entryData, setEntryData] = useEntryDataState(draftArticleId);
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const positionPropsInfo: PositionInfoProps = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
    shellKey,
    shellIndex,
  };

  const {
    isFocused,
    isClearShell,
    isEnableEdit,
    isAlertShell,
    focusStatus,
    columnSize,
    isClearTable,
    isDeleteTable,
    handleFocusOutShell,
  } = useHandleVfAssetTransferTableCreatorShell(positionPropsInfo);
  const [isOpenTerritoryControlledModal, setIsOpenTerritoryControlledModal] =
    useState(false);
  const { isPressed } = useCheckPressedShell();

  const { shellValue } = useGetInfoOfShell(groupTableKey, articleSection);
  const { register } = useScroll();

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-dropdown="list-box"]'))
        return;
      if ((e.target as HTMLElement).closest("[data-button]")) return;
      if (isFocused) handleFocusOutShell();
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleFocusOutShell, isFocused]);

  const shellWidth = ASSET_TRANSFER_COLUMN_WIDTH[shellKey] || "12.8rem";

  // VF Asset Transfer 텍스트 셀은 모두 직접 입력 가능 (LA&SC 의 Axis/Rights Scope 등 비편집 컬럼 없음)
  const DISABLE_TEXT_EVENT_SHELL_KEYS: string[] = [];

  const ENTER_DATA_BY_CONTROLLED_TERRITORY_MODAL_SHELL_KEYS = [
    "Territory Include",
    "Territory Exclude",
  ];
  if (ENTER_DATA_BY_CONTROLLED_TERRITORY_MODAL_SHELL_KEYS.includes(shellKey)) {
    const shellValuePlaceholder = getShellTitle(articleSection, shellKey);
    const shellValueText = shellValue(
      shellKey,
      tableIndex,
      groupShellIndex,
      shellIndex,
      subGroupShellIndex,
    )
      .value.map(({ text }) => text)
      .join(", ");
    const isShellValue = !!shellValueText;
    return (
      <S.ShellWrapper
        column={columnSize}
        width={shellWidth}
        maxWidth={shellWidth}
        {...register(
          `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${shellKey}__${shellIndex}`,
        )}
      >
        <S.ClickableShellBorderWrapper
          onClick={() => setIsOpenTerritoryControlledModal(true)}
          $isModified={shellValue(
            shellKey,
            tableIndex,
            groupShellIndex,
            shellIndex,
            subGroupShellIndex,
          ).value.some(({ is_modified }) => is_modified)}
          $isPlaceholder={!isShellValue}
        >
          {isShellValue ? shellValueText : shellValuePlaceholder}
        </S.ClickableShellBorderWrapper>
        {isOpenTerritoryControlledModal && (
          <VfAssetTransferTerritoryModal
            onClose={() => setIsOpenTerritoryControlledModal(false)}
            selectedInclude={shellValue(
              "Territory Include",
              tableIndex,
              groupShellIndex,
              shellIndex,
              subGroupShellIndex,
            ).value.flatMap(({ text }) => text.split(", "))}
            selectedExclude={shellValue(
              "Territory Exclude",
              tableIndex,
              groupShellIndex,
              shellIndex,
              subGroupShellIndex,
            ).value.flatMap(({ text }) => text.split(", "))}
            saveSelectedData={(
              selectedInclude: string[],
              selectedExclude: string[],
            ) => {
              const changedEntryData = {
                ...entryData,
                [ENTRY_DATA_SECTION_KEYS.deals]: {
                  ...entryData?.[ENTRY_DATA_SECTION_KEYS.deals],
                  "Licensee Table": entryData?.[
                    ENTRY_DATA_SECTION_KEYS.deals
                  ]?.["Licensee Table"]?.map((licenseeItem, licenseeIndex) => {
                    if (licenseeIndex !== tableIndex) return licenseeItem;
                    return {
                      ...licenseeItem,
                      "Rights Scope": licenseeItem["Rights Scope"]?.map(
                        (rightsScopeItem, rightsScopeIndex) => {
                          if (rightsScopeIndex !== groupShellIndex)
                            return rightsScopeItem;
                          return {
                            ...rightsScopeItem,
                            "Territory Include": {
                              ...rightsScopeItem?.["Territory Include"],
                              value: selectedInclude.map((include) => ({
                                text: include,
                                is_modified: true,
                              })),
                            },
                            "Territory Exclude": {
                              ...rightsScopeItem?.["Territory Exclude"],
                              value: selectedExclude.map((exclude) => ({
                                text: exclude,
                                is_modified: true,
                              })),
                            },
                          };
                        },
                      ),
                    };
                  }),
                },
              } as ArticleEntryData;
              setEntryData(() => changedEntryData);

              changeCurrentHistory({
                entryData: changedEntryData,
                changedDataInfo: {
                  target: "value",
                  action: "update",
                  position: {
                    groupTableKey,
                    groupTableIndex,
                    tableKey,
                    tableIndex,
                    groupShellKey,
                    groupShellIndex,
                    shellKey,
                    shellIndex,
                    value: null,
                    valueIndex: null,
                  },
                },
              });
            }}
          />
        )}
      </S.ShellWrapper>
    );
  }
  return (
    <S.ShellWrapper
      column={columnSize}
      width={shellWidth}
      maxWidth={shellWidth}
      {...register(
        `${groupTableKey}__${groupTableIndex}__${tableKey}__${tableIndex}__${groupShellKey}__${groupShellIndex}__${shellKey}__${shellIndex}`,
      )}
    >
      <S.ShellBorderWrapper
        isFocused={isFocused && focusStatus.type !== "dragging"}
        isClearStatus={
          isClearShell ||
          isClearGroupShell ||
          isClearSubGroupShell ||
          isClearTable ||
          isFocusGroupShellContainer
        }
        isDeleteStatus={
          isDeleteGroupShell || isDeleteSubGroupShell || isDeleteTable
        }
        isAlertStatus={isAlertShell || isAlertGroupShell}
        isDisableEdit={!isEnableEdit}
        $isDragging={isFocused && focusStatus.type === "dragging"}
        $isPressed={isPressed}
        onClick={(e) => {
          e.stopPropagation();
          if (DISABLE_TEXT_EVENT_SHELL_KEYS.includes(shellKey)) return;
          const contenteditable = (
            e.currentTarget as HTMLElement
          ).querySelector('[contenteditable="true"]');
          if (!(contenteditable instanceof HTMLElement)) return;
          contenteditable.focus();
          const sel = window.getSelection();
          if (sel && sel.toString().length > 0) return;
          const textNode = contenteditable.firstChild || contenteditable;
          const textLength = textNode.textContent?.length || 0;
          const range = document.createRange();
          range.setStart(textNode, textLength);
          range.setEnd(textNode, textLength);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }}
        onMouseDown={(e) => {
          if (DISABLE_TEXT_EVENT_SHELL_KEYS.includes(shellKey)) return;
          const contenteditable = (
            e.currentTarget as HTMLElement
          ).querySelector('[contenteditable="true"]');
          if (contenteditable instanceof HTMLElement) contenteditable.focus();
        }}
        $isTextEvent={!DISABLE_TEXT_EVENT_SHELL_KEYS.includes(shellKey)}
      >
        <TableCreatorTextFieldContainer
          placeholder={
            getShellPlaceholder(articleSection, shellKey) ||
            getShellTitle(articleSection, shellKey)
          }
          shellValue={shellValue(
            shellKey,
            tableIndex,
            groupShellIndex,
            shellIndex,
            subGroupShellIndex,
          )}
          shellKey={shellKey}
          shellIndex={shellIndex}
          groupTableKey={groupTableKey}
          groupTableIndex={groupTableIndex}
          groupShellKey={groupShellKey}
          groupShellIndex={groupShellIndex}
          subGroupShellKey={subGroupShellKey}
          subGroupShellIndex={subGroupShellIndex}
          tableKey={tableKey}
          tableIndex={tableIndex}
          articleSectionKey={articleSection}
        />
      </S.ShellBorderWrapper>
    </S.ShellWrapper>
  );
};

export default VfAssetTransferShell;
